import type {} from '@deepseek-ai/dsh-client-locale/client'
import type { TranslateNS } from '@deepseek-ai/dsh-client-ui-slots'
import { EARS_ERROR_CODES, type EarsErrorCode } from '../errors.js'

export const LOCALE_NAMESPACE = 'settings.dshEars' as const

/** English-only documentation rule exception: localeZh is the registered Simplified Chinese source of truth. */
export const localeZh = {
  polishPrompt: '润色提示词',
  polishPromptHint: '留空使用内置默认',
  promptPlaceholder: '输入自定义润色提示词…',
  promptViewDefault: '查看默认',
  promptReset: '恢复默认',
  promptTooLong: '自定义润色提示词不能超过 4000 个字符',
  promptCount: '{count} / {max}',
  voiceStart: '开始语音输入',
  voiceStop: '停止语音输入',
  voiceStarting: '正在启动…',
  voiceRecording: '正在识别',
  voiceBusy: '语音处理中',
  voiceDiscard: '丢弃这次语音',
  voiceTranscribing: '正在转写…',
  voicePolishing: '正在润色…',
  voicePolishFailed: '润色失败，已保留原文',
  voiceError: '请检查配置后重试',
  voiceUpstreamAsr: '语音识别服务错误：{detail}',
  voiceUpstreamPolish: '润色服务错误：{detail}',
  voiceUnavailable: '语音输入不可用',
  voiceUnavailableWebSpeech: '当前浏览器不支持语音输入',
  voiceUnavailableRecorder: '当前浏览器不支持所选后端的音频录制',
  displayName: '显示名称',
  displayNameHint: '设置页侧边栏的显示名称',
  displayNamePlugin: 'dsh-ears',
  displayNameVoice: '语音',
  description: '语音识别与文本润色',
  tabs: '设置分组',
  groupRecognition: '识别',
  groupPolishing: '润色',
  groupAbout: '关于',
  aboutRepository: '仓库',
  aboutVersion: '版本',
  aboutLicense: '许可证',
  aboutCompat: '兼容的 dsh 版本',
  aboutCheck: '更新',
  aboutCheckAction: '检查更新',
  aboutChecking: '正在检查…',
  aboutUpToDate: '已是最新',
  aboutUpdateAvailable: '有新版本：{version}',
  aboutUnpublished: '尚未发布，无法检查更新',
  aboutCheckFailed: '检查更新失败',
  aboutCopyCommand: '复制命令',
  backend: '识别后端',
  backendHintWebSpeech: '浏览器实时识别',
  backendHintLocalWhisper: '本地Whisper推理',
  backendHintGroq: 'Groq Whisper',
  backendHintDeepgram: 'Deepgram 语音识别',
  backendHintBailian: '阿里云百炼语音识别',
  backendHintTencent: '腾讯云语音识别',
  backendHintMimo: '小米 MiMo 语音识别',
  backendHintSiliconflow: '硅基流动语音识别',
  backendHintVolcengine: '火山引擎语音识别',
  backendHintCustom: 'OpenAI 兼容的转写接口',
  webSpeechBackend: 'Web Speech',
  localWhisperBackend: '本地 Whisper',
  groupLocal: '本地',
  groupCloud: '云提供商',
  groqProvider: 'Groq',
  deepgramProvider: 'Deepgram',
  bailianProvider: '阿里云百炼',
  tencentProvider: '腾讯云',
  mimoProvider: '小米 MiMo',
  siliconflowProvider: '硅基流动',
  volcengineProvider: '火山引擎',
  customProvider: '自定义 OpenAI 兼容接口',
  tencentService: '服务类型',
  tencentServiceHint: '选择腾讯云语音识别产品',
  tencentRecordingService: '标准录音文件识别',
  tencentRealtimeService: '实时语音识别',
  tencentAppId: 'AppID',
  tencentAppIdHint: '腾讯云 API 密钥管理中的 AppID',
  tencentSecretId: 'SecretID',
  tencentSecretIdHint: '腾讯云 API 密钥管理中的 SecretID',
  tencentSecretKey: 'SecretKey',
  tencentSecretKeyHint: '密钥只保存、不显示；留空保持原值',
  tencentEngineType: '算法引擎',
  tencentEngineTypeHint: '填写腾讯云 EngineModel，如 16k_zh、16k_en',
  deepgramService: '服务类型',
  deepgramServiceHint: '选择 Deepgram 语音识别产品',
  deepgramRecordingService: '预录音频',
  deepgramRealtimeService: '实时音频',
  deepgramModelHint: '支持的 Deepgram 模型名，如 "nova-3"',
  deepgramModelSelectHint: '从 Deepgram 的模型列表或自定义模型中选择',
  customModelOption: '自定义模型…',
  customModelName: '自定义模型名称',
  customModelInputHint: '输入 Deepgram 支持的模型名或自定义定制模型',
  bailianHost: 'API 地址',
  bailianHostHint: '填写控制台业务空间的 API Host（如 https://ws-xxxx.cn-beijing.maas.aliyuncs.com），可保留 /compatible-mode/v1 后缀',
  bailianModelHint: '填写模型 ID（如 qwen3-asr-flash、qwen-audio-3.1-asr-flash），不要填显示名称或 -filetrans/-streaming/-message 变体',
  mimoService: '接入方式',
  mimoServiceHint: '选择标准 API 或 Token Plan 订阅',
  mimoApiService: '标准 API',
  mimoTokenPlanService: 'Token Plan 订阅',
  mimoApiKeyHint: '请输入API密钥或TokenPlan密钥',
  mimoCluster: '服务集群',
  mimoClusterHint: '选择 Token Plan 所在区域集群',
  mimoClusterCn: '中国集群 (cn)',
  mimoClusterSgp: '新加坡集群 (sgp)',
  mimoClusterAms: '欧洲集群 (ams)',
  mimoModelHint: 'MiMo 语音识别模型，如 "mimo-v2.5-asr"',
  volcengineService: '服务类型',
  volcengineServiceHint: '选择火山引擎语音识别产品',
  volcengineRecordingService: '豆包录音文件识别',
  volcengineRealtimeService: '豆包单向流式语音识别',
  volcengineApiKeyHint: '火山引擎控制台 API Key 管理中的密钥；只保存、不显示',
  volcengineRealtimeModel: '流式模型版本',
  volcengineRealtimeModelHint: '单向流式语音识别的资源 ID',
  volcengineModelSeedasrSaucDuration: '豆包流式语音识别模型 2.0（小时版）',
  volcengineModelBigasrSaucDuration: '豆包流式语音识别模型 1.0（小时版）',
  volcengineRecordingModel: '文件识别模型版本',
  volcengineRecordingModelHint: '录音文件识别的资源 ID',
  volcengineModelSeedasrAuc: '豆包录音文件识别模型 2.0',
  volcengineModelBigasrAuc: '豆包录音文件识别模型 1.0',
  volcengineLanguageHint: '留空使用默认中英文识别，可填写 zh-CN、en-US 等语种代码',
  localModel: 'Whisper 模型',
  localAcceleration: '推理模式',
  localAccelerationHint: '默认会按当前平台和可用 native variant 自动选择推理后端',
  localAccelerationUnavailable: '当前平台没有可用的 Whisper 原生后端',
  localAccelerationDefault: '默认',
  localAccelerationVulkan: 'Vulkan',
  localAccelerationCuda: 'CUDA',
  whisperDownloaded: '模型已下载',
  whisperNotDownloaded: '模型未下载',
  whisperDownloading: '下载中',
  whisperDownloadingProgress: '下载中 {percent}%',
  whisperChecking: '检测中…',
  whisperNativeUnavailable: '当前加速后端的 Whisper 运行时不可用',
  clickDownload: '下载',
  retryDownload: '重试下载',
  cancelDownload: '取消下载',
  deleteModel: '删除模型',
  confirmDeleteModel: '确认删除',
  cloudEndpoint: '转写接口地址',
  cloudEndpointHint: '完整的 /audio/transcriptions 接口地址',
  cloudModel: '云端模型',
  cloudModelHint: '该接口支持的转写模型名',
  cloudModelGroqHint: '从 Groq 获取的转写模型',
  cloudModelSiliconflowHint: '从硅基流动获取的转写模型',
  cloudModelFetchFailed: '获取模型列表失败',
  cloudModelStale: '所选模型不在最新列表中，可能已下线',
  cloudKey: 'API key',
  cloudKeyHint: '密钥只保存、不显示；留空保持原值',
  cloudKeyConfigured: '已配置',
  cloudKeyNotConfigured: '未配置',
  cloudKeyClearPending: '即将自动清除',
  clearKey: '清除',
  undoClearKey: '撤销',
  backendUnavailable: '当前后端不可用：{detail}',
  localUnavailable: '当前加速后端的 Whisper 运行时不可用',
  cloudUnavailable: '请选择云端模型并配置 API key',
  language: '识别语言',
  webSpeechLanguageHint: '留空则跟随界面语言（如 zh-CN、en-US）',
  asrLanguageHint: '留空则自动检测语言（如 zh、en）',
  recordingLimit: '单次录音上限（秒）',
  recordingLimitHint: '达到上限时自动停止，1-600秒',
  groupGeneral: '通用',
  shortcutEnabled: '语音快捷键',
  shortcutEnabledHint: '设置语音输入的快捷键',
  soundsEnabled: '提示音',
  soundsEnabledHint: '开始和结束语音输入时播放提示音',
  shortcut: '快捷键',
  shortcutHint: '设置开始/结束语音输入的热键',
  shortcutCapture: '按下组合键…',
  shortcutCaptureHint: '按下新的组合键…（Esc 取消）',
  shortcutClear: '恢复默认',
  shortcutInvalidTypingKey: '该组合会输入字符：请为字母/数字加上 Ctrl 或 Shift 等修饰键（Alt/Option 组合会输入特殊字符）',
  shortcutInvalidFormat: '无效的快捷键组合',
  shortcutReserved: '该组合可能与浏览器或系统保留快捷键冲突',
  polishing: '文本润色',
  polishingHint: '对转写的文本进行润色整理',
  polishingOn: '开启',
  polishingOff: '关闭',
  provider: '模型提供方',
  providerHint: '选择润色模型的提供方',
  model: '模型',
  modelHint: '选择该提供方下的模型',
  reasoningEffort: '推理强度',
  reasoningEffortHint: '留空使用所选模型的默认推理设置',
  defaultEffort: '默认',
  providerPlaceholder: '选择提供方',
  modelPlaceholder: '选择模型',
  loadingModels: '正在读取 dsh 模型列表…',
  noModels: '当前没有可用的 dsh 模型，请先在 dsh 中配置模型',
  readOnly: '当前配置为只读，无法在此页保存',
  recoveredSettings: '检测到存储配置中有无效值，当前使用安全默认值；保存后将修复配置',
  loadFailed: '无法读取配置，请稍后重试',
  saveFailed: '保存失败，修改已保留',
  errorWhisperNativeUnavailable: '当前加速后端的 Whisper 运行时不可用',
  errorWhisperRestartRequired: '切换推理模式需要重启dsh',
  errorWhisperModelUnknown: '已安装的 Whisper 不认识模型“{model}”',
  errorWhisperModelUnverified: '模型文件未通过完整性检查，请重新下载',
  errorWhisperModelNotDownloaded: 'Whisper 模型尚未下载，请先下载模型',
  errorWhisperStateQueryFailed: '无法读取 Whisper 模型状态：{detail}',
  errorWhisperAlreadyDownloading: '另一个 Whisper 模型正在下载',
  errorWhisperDownloadFailed: 'Whisper 模型下载失败：{detail}',
  errorWhisperDownloadCleanupFailed: 'Whisper 模型下载失败，清理文件时出错：{detail}',
  errorWhisperCancelCleanupFailed: '取消 Whisper 下载时清理文件失败：{detail}',
  errorWhisperStillDownloading: '模型仍在下载',
  errorWhisperDeleteFailed: '删除 Whisper 模型失败：{detail}',
  errorCloudModelsTimedOut: '读取云端模型列表超时',
  errorCloudModelsHttpFailed: '读取云端模型列表失败（HTTP {status}）',
  errorCloudModelsInvalidJson: '云端模型列表返回了无效 JSON',
  errorCloudModelsNoModels: '云端模型列表中没有可用模型',
  errorCloudModelsTooLarge: '云端模型列表响应过大',
  errorCloudModelsListFailed: '读取云端模型列表失败：{detail}',
  errorBackendWebSpeechUnavailable: '当前浏览器可能不支持 Web Speech',
  errorBackendLocalUnavailable: '当前加速后端的 Whisper 运行时不可用',
  errorBackendCloudUnavailable: '请选择云端模型并配置 API key',
  errorAsrModelNotConfigured: '尚未配置云端转写模型',
  errorAsrModelUnsupported: '模型 {model} 使用文件转写或实时接口，语音输入仅支持同步模型，如 qwen-audio-3.1-asr-flash',
  errorAsrModelNotFound: '模型 {model} 不存在，请填写控制台中的模型 ID（区分大小写），并确认当前业务空间已开通该模型',
  errorAsrApiKeyNotConfigured: '尚未配置云端 API key',
  errorAsrEndpointInvalid: '云端转写接口必须使用 HTTP 或 HTTPS',
  errorAsrEndpointHasCredentials: '云端转写接口 URL 不能包含凭据',
  errorAsrAudioEmpty: '录音为空',
  errorAsrAudioInvalid: '录音数据无效',
  errorAsrAudioTooLarge: '录音文件过大',
  errorAsrMimeTypeMismatch: 'MiMo 语音识别仅支持 WAV 或 MP3 格式',
  errorAsrRequestTimedOut: '云端语音识别请求超时',
  errorAsrHttpFailed: '云端语音识别请求失败（HTTP {status}）',
  errorAsrInvalidResponse: '云端语音识别返回了无效响应',
  errorAsrNoTranscript: '云端语音识别未返回文本',
  errorAsrResponseTooLarge: '云端语音识别响应过大',
  errorAsrUnexpected: '语音识别失败：{detail}',
  errorAsrProviderUnknown: '未知的云端语音识别提供商',
  errorAsrServiceUnavailable: '所选云端语音识别服务与当前操作不匹配',
  errorAsrUnsupportedBackend: '当前语音识别后端不支持此操作',
  errorBrowserMediaUnavailable: '当前浏览器不支持音频录制',
  errorPolishTimedOut: '文本润色请求超时',
  errorPolishNoText: '润色服务未返回文本',
  errorPolishTooLarge: '润色服务返回的文本过长',
  errorPolishUnexpected: '润色请求失败：{detail}',
  errorPolishRouteFailed: 'dsh 润色模型未能完成请求',
  errorPolishSettingsUnavailable: 'dsh-ears 配置不可用',
} as const

export type LocaleKey = keyof typeof localeZh

export const localeEn = {
  polishPrompt: 'Polish prompt',
  polishPromptHint: 'Leave blank to use the built-in default',
  promptPlaceholder: 'Type your custom polish prompt…',
  promptViewDefault: 'View default',
  promptReset: 'Reset to default',
  promptTooLong: 'The custom polish prompt cannot exceed 4000 characters',
  promptCount: '{count} / {max}',
  voiceStart: 'Start voice input',
  voiceStop: 'Stop voice input',
  voiceStarting: 'Starting…',
  voiceRecording: 'Listening',
  voiceBusy: 'Processing',
  voiceDiscard: 'Discard this voice input',
  voiceTranscribing: 'Transcribing…',
  voicePolishing: 'Polishing…',
  voicePolishFailed: 'Polishing failed; the original transcript is kept',
  voiceError: 'Check the configuration and try again',
  voiceUpstreamAsr: 'Recognition service error: {detail}',
  voiceUpstreamPolish: 'Polishing service error: {detail}',
  voiceUnavailable: 'Voice input unavailable',
  voiceUnavailableWebSpeech: 'Voice input is unavailable in this browser',
  voiceUnavailableRecorder: 'This browser does not support audio recording for the selected backend',
  displayName: 'Display name',
  displayNameHint: 'Display name shown in the settings sidebar',
  displayNamePlugin: 'dsh-ears',
  displayNameVoice: 'Voice',
  description: 'Speech recognition and text polishing',
  tabs: 'Settings sections',
  groupRecognition: 'Recognition',
  groupPolishing: 'Polishing',
  groupAbout: 'About',
  aboutRepository: 'Repository',
  aboutVersion: 'Version',
  aboutLicense: 'License',
  aboutCompat: 'Compatible dsh versions',
  aboutCheck: 'Updates',
  aboutCheckAction: 'Check for updates',
  aboutChecking: 'Checking…',
  aboutUpToDate: 'You are on the latest version',
  aboutUpdateAvailable: 'A newer version is available: {version}',
  aboutUnpublished: 'Not published yet, so updates cannot be checked',
  aboutCheckFailed: 'Could not check for updates',
  aboutCopyCommand: 'Copy command',
  backend: 'Recognition backend',
  backendHintWebSpeech: 'Live recognition in the browser',
  backendHintLocalWhisper: 'Local Whisper inference',
  backendHintGroq: 'Groq Whisper',
  backendHintDeepgram: 'Deepgram speech recognition',
  backendHintBailian: 'Alibaba Cloud Model Studio speech recognition',
  backendHintTencent: 'Tencent Cloud ASR',
  backendHintMimo: 'Xiaomi MiMo speech recognition',
  backendHintSiliconflow: 'SiliconFlow speech recognition',
  backendHintVolcengine: 'Volcengine speech recognition',
  backendHintCustom: 'An OpenAI-compatible transcription endpoint',
  webSpeechBackend: 'Web Speech',
  localWhisperBackend: 'Local Whisper',
  groupLocal: 'Local',
  groupCloud: 'Cloud providers',
  groqProvider: 'Groq',
  deepgramProvider: 'Deepgram',
  bailianProvider: 'Alibaba Cloud Model Studio',
  tencentProvider: 'Tencent Cloud',
  mimoProvider: 'Xiaomi MiMo',
  siliconflowProvider: 'SiliconFlow',
  volcengineProvider: 'Volcengine',
  customProvider: 'Custom OpenAI-compatible endpoint',
  tencentService: 'Service',
  tencentServiceHint: 'Select a Tencent Cloud ASR product',
  tencentRecordingService: 'Standard recording file recognition',
  tencentRealtimeService: 'Real-time speech recognition',
  tencentAppId: 'AppID',
  tencentAppIdHint: 'AppID from Tencent Cloud API key management',
  tencentSecretId: 'SecretID',
  tencentSecretIdHint: 'SecretID from Tencent Cloud API key management',
  tencentSecretKey: 'SecretKey',
  tencentSecretKeyHint: 'Stored keys are never shown; leave blank to keep the current key',
  tencentEngineType: 'Algorithm engine',
  tencentEngineTypeHint: 'Enter a Tencent Cloud EngineModel, e.g. 16k_zh or 16k_en',
  deepgramService: 'Service',
  deepgramServiceHint: 'Select a Deepgram ASR product',
  deepgramRecordingService: 'Pre-Recorded Audio',
  deepgramRealtimeService: 'Live Audio',
  deepgramModelHint: 'Deepgram ASR model name, e.g. "nova-3"',
  deepgramModelSelectHint: 'Choose from curated Deepgram models or custom models',
  customModelOption: 'Custom model…',
  customModelName: 'Custom model name',
  customModelInputHint: 'Enter any Deepgram model identifier or custom trained model',
  bailianHost: 'API host',
  bailianHostHint: 'Enter the workspace API host from the console (e.g. https://ws-xxxx.cn-beijing.maas.aliyuncs.com); a /compatible-mode/v1 suffix is allowed',
  bailianModelHint: 'Enter the model ID (e.g. qwen3-asr-flash or qwen-audio-3.1-asr-flash), not the display name or a -filetrans/-streaming/-message variant',
  mimoService: 'Access method',
  mimoServiceHint: 'Select standard API or Token Plan subscription',
  mimoApiService: 'Standard API',
  mimoTokenPlanService: 'Token Plan',
  mimoApiKeyHint: 'Enter your API key or Token Plan key',
  mimoCluster: 'Cluster',
  mimoClusterHint: 'Select the Token Plan regional cluster',
  mimoClusterCn: 'China (cn)',
  mimoClusterSgp: 'Singapore (sgp)',
  mimoClusterAms: 'Europe (ams)',
  mimoModelHint: 'MiMo ASR model, e.g. "mimo-v2.5-asr"',
  volcengineService: 'Service',
  volcengineServiceHint: 'Select a Volcengine ASR product',
  volcengineRecordingService: 'Doubao audio file recognition',
  volcengineRealtimeService: 'Doubao one-way streaming ASR',
  volcengineApiKeyHint: 'Key from the Volcengine console API Key page; stored keys are never shown',
  volcengineRealtimeModel: 'Streaming model version',
  volcengineRealtimeModelHint: 'Resource id for one-way streaming recognition',
  volcengineModelSeedasrSaucDuration: 'Doubao streaming ASR model 2.0 (duration)',
  volcengineModelBigasrSaucDuration: 'Doubao streaming ASR model 1.0 (duration)',
  volcengineRecordingModel: 'Recording model version',
  volcengineRecordingModelHint: 'Resource id for recording file recognition',
  volcengineModelSeedasrAuc: 'Doubao audio file recognition model 2.0',
  volcengineModelBigasrAuc: 'Doubao audio file recognition model 1.0',
  volcengineLanguageHint: 'Leave empty for the default Chinese/English recognition, or enter a code such as zh-CN or en-US',
  localModel: 'Whisper model',
  localAcceleration: 'Inference mode',
  localAccelerationHint: 'Default automatically selects a Whisper backend from the platform and installed native variants',
  localAccelerationUnavailable: 'No Whisper native backend is available for this platform',
  localAccelerationDefault: 'Default',
  localAccelerationVulkan: 'Vulkan',
  localAccelerationCuda: 'CUDA',
  whisperDownloaded: 'Model downloaded',
  whisperNotDownloaded: 'Not downloaded',
  whisperDownloading: 'Downloading',
  whisperDownloadingProgress: 'Downloading {percent}%',
  whisperChecking: 'Checking…',
  whisperNativeUnavailable: 'The Whisper runtime is unavailable for the selected acceleration',
  clickDownload: 'Download',
  retryDownload: 'Retry download',
  cancelDownload: 'Cancel download',
  deleteModel: 'Delete model',
  confirmDeleteModel: 'Confirm delete',
  cloudEndpoint: 'Transcription endpoint',
  cloudEndpointHint: 'Full /audio/transcriptions endpoint URL',
  cloudModel: 'Cloud model',
  cloudModelHint: 'Transcription model supported by the endpoint',
  cloudModelGroqHint: 'Transcription models fetched from Groq',
  cloudModelSiliconflowHint: 'Transcription models fetched from SiliconFlow',
  cloudModelFetchFailed: 'Could not fetch the model list',
  cloudModelStale: 'The selected model is not in the latest list; it may be retired',
  cloudKey: 'API key',
  cloudKeyHint: 'Stored keys are never shown; leave blank to keep the current key',
  cloudKeyConfigured: 'Configured',
  cloudKeyNotConfigured: 'Not configured',
  cloudKeyClearPending: 'Will be cleared automatically',
  clearKey: 'Clear',
  undoClearKey: 'Undo',
  backendUnavailable: 'The selected backend is unavailable: {detail}',
  localUnavailable: 'The Whisper runtime is unavailable for the selected acceleration',
  cloudUnavailable: 'Choose a cloud model and configure the API key',
  language: 'Recognition language',
  webSpeechLanguageHint: 'Leave blank to follow the interface language (for example, zh-CN, en-US)',
  asrLanguageHint: 'Leave blank for automatic language detection (for example zh, en)',
  recordingLimit: 'Recording limit (seconds)',
  recordingLimitHint: 'Stops automatically at the limit, 1–600 seconds',
  groupGeneral: 'General',
  shortcutEnabled: 'Voice shortcut',
  shortcutEnabledHint: 'Set the voice input shortcut',
  soundsEnabled: 'Sound cues',
  soundsEnabledHint: 'Play a click when voice input starts and stops',
  shortcut: 'Keyboard shortcut',
  shortcutHint: 'Set the hotkey that starts and stops voice input',
  shortcutCapture: 'Press keys…',
  shortcutCaptureHint: 'Press the new key combination… (Esc to cancel)',
  shortcutClear: 'Reset to default',
  shortcutInvalidTypingKey: 'This combination produces text: add Ctrl or Shift to letters/digits (Alt/Option combinations type special characters)',
  shortcutInvalidFormat: 'Invalid shortcut combination',
  shortcutReserved: 'This combination may conflict with a browser or system reserved shortcut',
  polishing: 'Text polishing',
  polishingHint: 'Polish and organize the transcribed text',
  polishingOn: 'On',
  polishingOff: 'Off',
  provider: 'Provider',
  providerHint: 'Choose the provider of the polishing model',
  model: 'Model',
  modelHint: 'Choose a model under that provider',
  reasoningEffort: 'Reasoning effort',
  reasoningEffortHint: 'Leave empty to use the selected model default',
  defaultEffort: 'Default',
  providerPlaceholder: 'Choose provider',
  modelPlaceholder: 'Choose model',
  loadingModels: 'Loading dsh model list…',
  noModels: 'No dsh models are available, configure a model in dsh first',
  readOnly: 'The current configuration is read-only and cannot be saved from this page',
  recoveredSettings: 'Invalid stored settings were found; safe defaults are shown and saving will repair the configuration',
  loadFailed: 'Could not load the plugin configuration, try again later',
  saveFailed: 'Save failed, your changes were kept',
  errorWhisperNativeUnavailable: 'The Whisper runtime for the selected acceleration is unavailable',
  errorWhisperRestartRequired: 'Restart dsh to switch the inference mode',
  errorWhisperModelUnknown: 'The installed Whisper does not know the model “{model}”',
  errorWhisperModelUnverified: 'The model file could not be verified, download it again',
  errorWhisperModelNotDownloaded: 'The Whisper model is not downloaded, download it first',
  errorWhisperStateQueryFailed: 'Could not read the Whisper model state: {detail}',
  errorWhisperAlreadyDownloading: 'Another Whisper model is already downloading',
  errorWhisperDownloadFailed: 'Whisper model download failed: {detail}',
  errorWhisperDownloadCleanupFailed: 'Whisper model download failed while cleaning up: {detail}',
  errorWhisperCancelCleanupFailed: 'Whisper download cleanup failed while cancelling: {detail}',
  errorWhisperStillDownloading: 'The model is still downloading',
  errorWhisperDeleteFailed: 'Could not delete the Whisper model: {detail}',
  errorCloudModelsTimedOut: 'Loading the cloud model list timed out',
  errorCloudModelsHttpFailed: 'Could not load the cloud model list (HTTP {status})',
  errorCloudModelsInvalidJson: 'The cloud model list returned invalid JSON',
  errorCloudModelsNoModels: 'The cloud model list contains no usable models',
  errorCloudModelsTooLarge: 'The cloud model list response is too large',
  errorCloudModelsListFailed: 'Could not load the cloud model list: {detail}',
  errorBackendWebSpeechUnavailable: 'Web Speech may be unavailable in this browser',
  errorBackendLocalUnavailable: 'The Whisper runtime is unavailable for the selected acceleration',
  errorBackendCloudUnavailable: 'Choose a cloud model and configure the API key',
  errorAsrModelNotConfigured: 'The cloud transcription model is not configured',
  errorAsrModelUnsupported: 'Model {model} uses the file-transcription or realtime API; voice input needs a synchronous model such as qwen-audio-3.1-asr-flash',
  errorAsrModelNotFound: 'Model {model} does not exist; enter the console model ID exactly (it is case-sensitive) and confirm it is enabled in this workspace',
  errorAsrApiKeyNotConfigured: 'The cloud ASR API key is not configured',
  errorAsrEndpointInvalid: 'The cloud transcription endpoint must use HTTP or HTTPS',
  errorAsrEndpointHasCredentials: 'The cloud transcription endpoint URL cannot contain credentials',
  errorAsrAudioEmpty: 'The recorded audio is empty',
  errorAsrAudioInvalid: 'The recorded audio data is invalid',
  errorAsrAudioTooLarge: 'The recorded audio is too large',
  errorAsrMimeTypeMismatch: 'MiMo speech recognition supports WAV or MP3 audio only',
  errorAsrRequestTimedOut: 'The cloud recognition request timed out',
  errorAsrHttpFailed: 'The cloud recognition request failed (HTTP {status})',
  errorAsrInvalidResponse: 'The cloud recognition service returned an invalid response',
  errorAsrNoTranscript: 'The cloud recognition service returned no transcript',
  errorAsrResponseTooLarge: 'The cloud recognition response is too large',
  errorAsrUnexpected: 'The recognition request failed: {detail}',
  errorAsrProviderUnknown: 'The cloud recognition provider is unknown',
  errorAsrServiceUnavailable: 'The selected cloud ASR service does not match the current operation',
  errorAsrUnsupportedBackend: 'The selected recognition backend does not support this operation',
  errorBrowserMediaUnavailable: 'This browser does not support audio recording',
  errorPolishTimedOut: 'The polishing request timed out',
  errorPolishNoText: 'The polishing service returned no text',
  errorPolishTooLarge: 'The polishing response is too large',
  errorPolishUnexpected: 'The polishing request failed: {detail}',
  errorPolishRouteFailed: 'The dsh polishing model did not complete the request',
  errorPolishSettingsUnavailable: 'The dsh-ears configuration is unavailable',
} satisfies Record<LocaleKey, string>

export type Translate = TranslateNS<typeof LOCALE_NAMESPACE>

export function fallbackTranslate(key: string, params?: Record<string, unknown>): string {
  const template = localeEn[key as keyof typeof localeEn] ?? key
  return params === undefined ? template : template.replace(/\{(\w+)\}/g, (match, name: string) => name in params ? String(params[name]) : match)
}

const ERROR_LOCALE_KEYS: Record<EarsErrorCode, LocaleKey> = {
  [EARS_ERROR_CODES.whisperNativeUnavailable]: 'errorWhisperNativeUnavailable',
  [EARS_ERROR_CODES.whisperRestartRequired]: 'errorWhisperRestartRequired',
  [EARS_ERROR_CODES.whisperModelUnknown]: 'errorWhisperModelUnknown',
  [EARS_ERROR_CODES.whisperModelUnverified]: 'errorWhisperModelUnverified',
  [EARS_ERROR_CODES.whisperModelNotDownloaded]: 'errorWhisperModelNotDownloaded',
  [EARS_ERROR_CODES.whisperStateQueryFailed]: 'errorWhisperStateQueryFailed',
  [EARS_ERROR_CODES.whisperAlreadyDownloading]: 'errorWhisperAlreadyDownloading',
  [EARS_ERROR_CODES.whisperDownloadFailed]: 'errorWhisperDownloadFailed',
  [EARS_ERROR_CODES.whisperDownloadCleanupFailed]: 'errorWhisperDownloadCleanupFailed',
  [EARS_ERROR_CODES.whisperCancelCleanupFailed]: 'errorWhisperCancelCleanupFailed',
  [EARS_ERROR_CODES.whisperStillDownloading]: 'errorWhisperStillDownloading',
  [EARS_ERROR_CODES.whisperDeleteFailed]: 'errorWhisperDeleteFailed',
  [EARS_ERROR_CODES.cloudModelsTimedOut]: 'errorCloudModelsTimedOut',
  [EARS_ERROR_CODES.cloudModelsHttpFailed]: 'errorCloudModelsHttpFailed',
  [EARS_ERROR_CODES.cloudModelsInvalidJson]: 'errorCloudModelsInvalidJson',
  [EARS_ERROR_CODES.cloudModelsNoModels]: 'errorCloudModelsNoModels',
  [EARS_ERROR_CODES.cloudModelsTooLarge]: 'errorCloudModelsTooLarge',
  [EARS_ERROR_CODES.cloudModelsListFailed]: 'errorCloudModelsListFailed',
  [EARS_ERROR_CODES.backendWebSpeechUnavailable]: 'errorBackendWebSpeechUnavailable',
  [EARS_ERROR_CODES.backendLocalUnavailable]: 'errorBackendLocalUnavailable',
  [EARS_ERROR_CODES.backendCloudUnavailable]: 'errorBackendCloudUnavailable',
  [EARS_ERROR_CODES.asrModelNotConfigured]: 'errorAsrModelNotConfigured',
  [EARS_ERROR_CODES.asrModelUnsupported]: 'errorAsrModelUnsupported',
  [EARS_ERROR_CODES.asrModelNotFound]: 'errorAsrModelNotFound',
  [EARS_ERROR_CODES.asrApiKeyNotConfigured]: 'errorAsrApiKeyNotConfigured',
  [EARS_ERROR_CODES.asrEndpointInvalid]: 'errorAsrEndpointInvalid',
  [EARS_ERROR_CODES.asrEndpointHasCredentials]: 'errorAsrEndpointHasCredentials',
  [EARS_ERROR_CODES.asrAudioEmpty]: 'errorAsrAudioEmpty',
  [EARS_ERROR_CODES.asrAudioInvalid]: 'errorAsrAudioInvalid',
  [EARS_ERROR_CODES.asrAudioTooLarge]: 'errorAsrAudioTooLarge',
  [EARS_ERROR_CODES.asrMimeTypeMismatch]: 'errorAsrMimeTypeMismatch',
  [EARS_ERROR_CODES.asrRequestTimedOut]: 'errorAsrRequestTimedOut',
  [EARS_ERROR_CODES.asrHttpFailed]: 'errorAsrHttpFailed',
  [EARS_ERROR_CODES.asrInvalidResponse]: 'errorAsrInvalidResponse',
  [EARS_ERROR_CODES.asrNoTranscript]: 'errorAsrNoTranscript',
  [EARS_ERROR_CODES.asrResponseTooLarge]: 'errorAsrResponseTooLarge',
  [EARS_ERROR_CODES.asrUnexpected]: 'errorAsrUnexpected',
  [EARS_ERROR_CODES.asrProviderUnknown]: 'errorAsrProviderUnknown',
  [EARS_ERROR_CODES.asrServiceUnavailable]: 'errorAsrServiceUnavailable',
  [EARS_ERROR_CODES.asrUnsupportedBackend]: 'errorAsrUnsupportedBackend',
  [EARS_ERROR_CODES.browserMediaUnavailable]: 'errorBrowserMediaUnavailable',
  [EARS_ERROR_CODES.polishTimedOut]: 'errorPolishTimedOut',
  [EARS_ERROR_CODES.polishNoText]: 'errorPolishNoText',
  [EARS_ERROR_CODES.polishTooLarge]: 'errorPolishTooLarge',
  [EARS_ERROR_CODES.polishUnexpected]: 'errorPolishUnexpected',
  [EARS_ERROR_CODES.polishRouteFailed]: 'errorPolishRouteFailed',
  [EARS_ERROR_CODES.polishSettingsUnavailable]: 'errorPolishSettingsUnavailable'
}

export function localizedErrorText(t: Translate, code: string | undefined | null, fallback: string, params?: Readonly<Record<string, string | number>>): string {
  if (code === undefined || code === null) return fallback
  const key = ERROR_LOCALE_KEYS[code as EarsErrorCode]
  if (key === undefined) return fallback
  const template = localeZh[key]
  for (const match of template.matchAll(/\{(\w+)\}/g)) {
    if (params === undefined || !Object.prototype.hasOwnProperty.call(params, match[1])) return fallback
  }
  return t(key, params)
}

declare module '@deepseek-ai/dsh-client-ui-slots' {
  interface LocaleNamespaceMap {
    'settings.dshEars': LocaleKey
  }
}
